import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTenantCompliance } from "../../hooks/useTenantCompliance";
import { ComplianceTypeCard } from "../../components/compliance/ComplianceTypeCard";
import { SyncStatusCard } from "../../components/compliance/SyncStatusCard";
import { Button } from "../../../../../ui/components/Button";
import { FeatherSettings } from "@subframe/core";
import { useToast } from "../../../../../shared/presentation/components";

export const ComplianceCalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const {
    groupedEntries,
    loadingCalendar,
    errorCalendar,
    fetchCalendarGrouped,
    syncStatus,
    loadingSyncStatus,
    syncing,
    fetchSyncStatus,
    triggerManualSync,
  } = useTenantCompliance();

  useEffect(() => {
    fetchCalendarGrouped();
    fetchSyncStatus();
  }, [fetchCalendarGrouped, fetchSyncStatus]);

  const handleManualSync = async () => {
    try {
      const result = await triggerManualSync();
      showSuccess(result.message || "Sync completed successfully");
    } catch (err: any) {
      showError(err.message || "Failed to sync compliance calendar");
    }
  };

  const loading = loadingCalendar || loadingSyncStatus;

  return (
    <div className="flex w-full flex-col items-start gap-6 p-8">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Compliance Calendar
            </span>
            <span className="text-body font-body text-subtext-color">
              View enabled compliance types and their calendar entries
            </span>
          </div>
        </div>
        <Button
          variant="neutral-primary"
          icon={<FeatherSettings />}
          onClick={() => navigate("/admin/compliance/types")}
        >
          Manage Types
        </Button>
      </div>

      {errorCalendar && (
        <div className="flex w-full flex-col items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
          <span className="text-sm font-medium text-red-800">
            {errorCalendar.message}
          </span>
        </div>
      )}

      {loading ? (
        <div className="flex w-full items-center justify-center p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-body font-body text-subtext-color">
              Loading calendar entries...
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Sync Status Card */}
          <SyncStatusCard
            syncStatus={syncStatus}
            loading={loadingSyncStatus}
            syncing={syncing}
            onManualSync={handleManualSync}
          />

          {/* Grouped Calendar Entries */}
          <div className="flex w-full flex-col items-start gap-4">
            {Object.keys(groupedEntries).map((typeId) => {
              const group = groupedEntries[typeId];
              return (
                <ComplianceTypeCard
                  key={typeId}
                  type={group.type}
                  entries={group.entries}
                />
              );
            })}

            {Object.keys(groupedEntries).length === 0 && (
              <div className="flex w-full items-center justify-center p-8 text-body font-body text-subtext-color">
                No enabled compliance types found. Please enable compliance
                types first.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
